/**
 * Arrow tool metadata registration
 * Feature: 049-critique-photo-markup
 */

import React from 'react';
import type { MarkupShape, ArrowShape } from '@/types/markup.types';
import { isArrowShape } from '@/types/markup.types';
import { registerTool } from '@/utils/markupToolRegistry';
import type { HandlePosition, ToolMetadata, ShapeRenderProps } from '@/utils/markupToolRegistry';
import { ArrowTool } from './ArrowTool';
import { ArrowIcon } from '../ToolIcons';
import { isHandDrawn } from '@/utils/roughRenderer';
import { HandDrawnLine, HandDrawnPolygon } from '../HandDrawnShapes';

/**
 * Calculate arrow head points for polygon rendering
 */
function calculateArrowHead(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  headSize: number = 3 // 3% of viewBox
): string {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const headAngle = Math.PI / 6; // 30 degrees

  const p1x = x2 - headSize * Math.cos(angle - headAngle);
  const p1y = y2 - headSize * Math.sin(angle - headAngle);

  const p2x = x2 - headSize * Math.cos(angle + headAngle);
  const p2y = y2 - headSize * Math.sin(angle + headAngle);

  return `${p1x},${p1y} ${x2},${y2} ${p2x},${p2y}`;
}

/**
 * Calculate arrow head points as array of tuples for Rough.js
 */
function calculateArrowHeadPoints(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  headSize: number = 3 // 3% of viewBox
): Array<[number, number]> {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const headAngle = Math.PI / 6; // 30 degrees

  const p1x = x2 - headSize * Math.cos(angle - headAngle);
  const p1y = y2 - headSize * Math.sin(angle - headAngle);

  const p2x = x2 - headSize * Math.cos(angle + headAngle);
  const p2y = y2 - headSize * Math.sin(angle + headAngle);

  return [
    [p1x, p1y],
    [x2, y2],
    [p2x, p2y],
  ];
}

/**
 * Arrow tool metadata
 */
const arrowToolMetadata: ToolMetadata = {
  type: 'arrow',
  label: 'Arrow',
  icon: ArrowIcon,
  Component: ArrowTool,
  zOrder: 0, // Default z-order (render on top of background effects)

  /**
   * Get handle positions for arrow
   * Handle 1: Start point (tail)
   * Handle 2: End point (head)
   */
  getHandles: (shape: MarkupShape): HandlePosition[] => {
    if (!isArrowShape(shape)) return [];

    return [
      {
        id: 'handle1',
        x: shape.geometry.x1,
        y: shape.geometry.y1,
        cursor: 'move',
        label: 'Move arrow start',
      },
      {
        id: 'handle2',
        x: shape.geometry.x2,
        y: shape.geometry.y2,
        cursor: 'move',
        label: 'Move arrow end',
      },
    ];
  },

  /**
   * Update arrow from handle drag
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  updateFromHandle: (
    shape: MarkupShape,
    handleId: 'handle1' | 'handle2',
    x: number,
    y: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isArrowShape(shape)) return shape;

    if (handleId === 'handle1') {
      // Dragging start point (image-relative: 0-100)
      return {
        ...shape,
        geometry: {
          x1: Math.max(0, Math.min(100, x)),
          y1: Math.max(0, Math.min(100, y)),
          x2: shape.geometry.x2,
          y2: shape.geometry.y2,
        },
      } as ArrowShape;
    } else {
      // Dragging end point (image-relative: 0-100)
      return {
        ...shape,
        geometry: {
          x1: shape.geometry.x1,
          y1: shape.geometry.y1,
          x2: Math.max(0, Math.min(100, x)),
          y2: Math.max(0, Math.min(100, y)),
        },
      } as ArrowShape;
    }
  },

  /**
   * Translate arrow by deltaX and deltaY
   *
   * BUG FIX (#480): Now uses image-relative coordinates (0-100 for both X and Y)
   */
  translateShape: (
    shape: MarkupShape,
    deltaX: number,
    deltaY: number,
    _viewBoxHeight: number // Kept for backwards compatibility, no longer used
  ): MarkupShape => {
    if (!isArrowShape(shape)) return shape;

    return {
      ...shape,
      geometry: {
        x1: Math.max(0, Math.min(100, shape.geometry.x1 + deltaX)),
        y1: Math.max(0, Math.min(100, shape.geometry.y1 + deltaY)),
        x2: Math.max(0, Math.min(100, shape.geometry.x2 + deltaX)),
        y2: Math.max(0, Math.min(100, shape.geometry.y2 + deltaY)),
      },
    } as ArrowShape;
  },

  /**
   * Render arrow shape as SVG
   */
  renderShape: ({ shape, isSelected, onMouseDown, viewBoxHeight }: ShapeRenderProps): React.ReactElement => {
    if (!isArrowShape(shape)) return React.createElement('g');

    // Calculate responsive fontSize based on smaller dimension
    const smallerDimension = Math.min(100, viewBoxHeight);
    const fontSize = smallerDimension * 0.04;
    const labelRadius = smallerDimension * 0.03;

    const labelX = (shape.geometry.x1 + shape.geometry.x2) / 2;
    const labelY = (shape.geometry.y1 + shape.geometry.y2) / 2 - 2;
    const className = `markup-shape ${isSelected ? 'selected' : ''}`;
    const style: React.CSSProperties = {
      cursor: isSelected ? 'grab' : 'pointer',
      pointerEvents: 'auto',
    };

    // Check if hand-drawn style is enabled
    const useHandDrawn = isHandDrawn(shape.strokeDasharray);

    // Calculate arrow head geometry
    const arrowHeadPoints = useHandDrawn
      ? calculateArrowHeadPoints(
          shape.geometry.x1,
          shape.geometry.y1,
          shape.geometry.x2,
          shape.geometry.y2
        )
      : calculateArrowHead(
          shape.geometry.x1,
          shape.geometry.y1,
          shape.geometry.x2,
          shape.geometry.y2
        );

    return React.createElement(
      'g',
      { key: shape.id },
      // Arrow line (hand-drawn or standard)
      useHandDrawn
        ? React.createElement(HandDrawnLine, {
            x1: shape.geometry.x1,
            y1: shape.geometry.y1,
            x2: shape.geometry.x2,
            y2: shape.geometry.y2,
            strokeColor: shape.strokeColor,
            strokeWidth: shape.strokeWidth,
            isSelected,
            onMouseDown,
            className,
          })
        : React.createElement('line', {
            className,
            x1: shape.geometry.x1,
            y1: shape.geometry.y1,
            x2: shape.geometry.x2,
            y2: shape.geometry.y2,
            stroke: shape.strokeColor,
            strokeWidth: isSelected ? shape.strokeWidth * 1.5 : shape.strokeWidth,
            strokeDasharray: shape.strokeDasharray,
            vectorEffect: 'non-scaling-stroke',
            style,
            onMouseDown,
          }),
      // Arrow head (hand-drawn or standard)
      useHandDrawn
        ? React.createElement(HandDrawnPolygon, {
            points: arrowHeadPoints as Array<[number, number]>,
            strokeColor: shape.strokeColor,
            strokeWidth: shape.strokeWidth,
            isSelected,
            onMouseDown,
            className,
          })
        : React.createElement('polygon', {
            points: arrowHeadPoints as string,
            fill: shape.strokeColor,
            stroke: shape.strokeColor,
            strokeWidth: 0.5,
            style,
            onMouseDown,
          }),
      // Shape number label background
      React.createElement('circle', {
        cx: labelX,
        cy: labelY,
        r: labelRadius,
        fill: 'black',
        fillOpacity: '0.5',
        stroke: 'white',
        strokeWidth: '0.3',
        vectorEffect: 'non-scaling-stroke',
        style,
        onMouseDown,
      }),
      // Shape number label text
      React.createElement(
        'text',
        {
          x: labelX,
          y: labelY,
          fontSize: String(fontSize),
          fontWeight: 'bold',
          fill: 'white',
          textAnchor: 'middle',
          dominantBaseline: 'central',
          style: { pointerEvents: 'none', userSelect: 'none' },
        },
        shape.number
      )
    );
  },
};

// Register the tool
registerTool(arrowToolMetadata);

export default arrowToolMetadata;
